/* ==========================================================================
   FLC Forge — Predictive Search
   ========================================================================== */

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input        = this.querySelector('[data-search-input]');
    this.clearBtn     = this.querySelector('[data-search-clear]');
    this.panel        = this.querySelector('[data-search-panel]');
    this.leftLabel    = this.querySelector('[data-search-left-label]');
    this.leftContent  = this.querySelector('[data-search-left-content]');
    this.pagesGroup   = this.querySelector('[data-search-pages-group]');
    this.pagesContent = this.querySelector('[data-search-pages-content]');
    this.rightLabel   = this.querySelector('[data-search-right-label]');
    this.resultsGrid  = this.querySelector('[data-search-results]');
    this.viewAllLink  = this.querySelector('[data-search-view-all]');
    this.viewAllLabel = this.querySelector('[data-search-view-all-label]');
    this.searchBar    = this.closest('.header-search');
    this.searchForm   = this.searchBar?.querySelector('form') || this.closest('form');
    this.abort        = null;

    // Save Liquid-rendered default state
    this.defaultLeftLabel   = this.leftLabel?.textContent || '';
    this.defaultLeftHTML    = this.leftContent?.innerHTML || '';
    this.defaultRightLabel  = this.rightLabel?.textContent || '';
    this.defaultResultsHTML = this.resultsGrid?.innerHTML || '';

    this.input?.addEventListener('input', FLC.debounce(() => this.handleInput(), 300));
    this.input?.addEventListener('focus', () => {
      if (this.input?.value.trim()) this.openPanel();
    });
    this.clearBtn?.addEventListener('click', () => this.clearSearch());

    // Handle form submission - append wildcard so storefront search does a
    // prefix match, matching the predictive dropdown's behaviour.
    this.searchForm?.addEventListener('submit', (e) => {
      const query = this.input?.value.trim() || '';
      if (query && !query.includes('*')) {
        e.preventDefault();
        const searchQuery = `${query}*`;
        const searchUrl = `${this.dataset.resultsUrl || '/search'}?q=${encodeURIComponent(searchQuery)}&type=${this.dataset.searchType || 'product'}`;
        window.location.href = searchUrl;
      }
    });

    // Close panel when SearchToggle removes is-open
    if (this.searchBar) {
      new MutationObserver(() => {
        if (!this.searchBar.classList.contains('is-open')) this.closePanel();
      }).observe(this.searchBar, { attributes: true, attributeFilter: ['class'] });
    }

    document.addEventListener('click', (e) => {
      if (!this.searchBar?.contains(e.target)) this.closePanel();
    });
  }

  openPanel() {
    this.panel?.removeAttribute('hidden');
    document.body.classList.add('search-panel-open');
  }

  closePanel() {
    this.panel?.setAttribute('hidden', '');
    this.viewAllLink?.setAttribute('hidden', '');
    document.body.classList.remove('search-panel-open');
  }

  clearSearch() {
    if (this.input) this.input.value = '';
    this.clearBtn?.classList.remove('is-visible');
    this.restoreDefaults();
    this.closePanel();
    this.input?.focus();
  }

  restoreDefaults() {
    if (this.leftLabel)    this.leftLabel.textContent    = this.defaultLeftLabel;
    if (this.leftContent)  this.leftContent.innerHTML    = this.defaultLeftHTML;
    if (this.rightLabel)   this.rightLabel.textContent   = this.defaultRightLabel;
    if (this.resultsGrid)  this.resultsGrid.innerHTML    = this.defaultResultsHTML;
    if (this.pagesContent) this.pagesContent.innerHTML   = '';
    this.pagesGroup?.setAttribute('hidden', '');
    this.panel?.removeAttribute('data-has-query');
    this.viewAllLink?.setAttribute('hidden', '');
    this.abort?.abort();
  }

  async handleInput() {
    const query = this.input?.value.trim();

    this.clearBtn?.classList.toggle('is-visible', !!query);

    if (!query) {
      this.restoreDefaults();
      this.closePanel();
      return;
    }

    this.openPanel();
    this.panel?.setAttribute('data-has-query', '');
    this.setLoading();
    this.abort?.abort();
    this.abort = new AbortController();

    try {
      // Fetch the section-rendered HTML so we get real product cards
      // (including sibling swatches) directly from Liquid. Shopify's
      // predictive-search endpoint automatically renders
      // `sections/predictive-search.liquid` when the Accept header is HTML.
      const url = `${this.dataset.searchUrl}?q=${encodeURIComponent(query)}&resources[type]=product,query,page&resources[limit]=6&section_id=predictive-search`;
      const res = await fetch(url, {
        signal: this.abort.signal,
        headers: { Accept: 'text/html' }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      this.renderFromSection(html, query);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Predictive search error:', err);
        this.restoreDefaults();
      }
    }
  }

  setLoading() {
    if (this.resultsGrid) {
      this.resultsGrid.innerHTML = '<div class="search-panel__loading" role="status" aria-label="Searching\u2026"></div>';
    }
  }

  renderFromSection(html, query) {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    // Suggestions (queries)
    const suggestions = doc.querySelector('[data-predictive-suggestions]');
    const hasSuggestions = suggestions && suggestions.querySelector('li');
    if (this.leftLabel) this.leftLabel.textContent = 'Suggestions';
    if (this.leftContent) {
      this.leftContent.innerHTML = hasSuggestions
        ? suggestions.innerHTML
        : '<li class="search-panel__empty">No suggestions found</li>';
    }

    // Pages
    const pagesEl = doc.querySelector('[data-predictive-pages]');
    const hasPages = pagesEl && pagesEl.querySelector('li');
    if (this.pagesContent) {
      this.pagesContent.innerHTML = hasPages
        ? pagesEl.innerHTML
        : '<li class="search-panel__empty">No pages found</li>';
    }
    this.pagesGroup?.removeAttribute('hidden');

    // Products grid (real product-card snippets)
    const productsEl = doc.querySelector('[data-predictive-results-container]');
    if (this.rightLabel) this.rightLabel.textContent = 'Products';
    if (this.resultsGrid) {
      if (productsEl) {
        this.resultsGrid.innerHTML = productsEl.innerHTML;
      } else {
        // Section response was missing or malformed — log to help debugging
        console.warn('Predictive search: [data-predictive-results-container] not found in response.', html.slice(0, 300));
        this.resultsGrid.innerHTML = `<p class="search-panel__no-results">No results for &ldquo;${this.esc(query)}&rdquo;</p>`;
      }
    }

    // View all link - append wildcard so the results page prefix-matches
    // (mirrors the dropdown). Skip only if the term already carries a '*'.
    if (this.viewAllLink && this.viewAllLabel) {
      const searchQuery = query.includes('*') ? query : `${query}*`;
      const href = `${this.dataset.resultsUrl || '/search'}?q=${encodeURIComponent(searchQuery)}&type=${this.dataset.searchType || 'product'}`;
      this.viewAllLink.href = href;
      this.viewAllLabel.textContent = 'View all results';
    }

    // Hide view-all when there are no products
    const hasProducts = !!this.resultsGrid?.querySelector('.product-card, [data-pc-sibling-card]');
    if (this.viewAllLink) {
      hasProducts
        ? this.viewAllLink.removeAttribute('hidden')
        : this.viewAllLink.setAttribute('hidden', '');
    }
  }

  resizeImage(url, width) {
    if (!url) return '';
    try {
      const u = new URL(url);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch {
      return url;
    }
  }

  esc(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}

customElements.define('predictive-search', PredictiveSearch);